import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../Store'
import { IUserModel } from '../models/User.model'

export interface ITariffModel {
  id: number
  name: string
  type: string
  max_keywords: number
  max_users: number
  max_projects: number
  used_keywords_count: number
  used_projects_count: number
  duration: number
  subscription_status: 'active' | 'grace_period' | 'suspended'
  subscription_start_date: string
  subscription_expiration_date: string
  stripe_cancel_at: string
  is_subscription_expired: boolean
  payment_method: string
  payment_periodicity: string
  price: string
  currency_symbol: string
}

export interface IProjectDetailsModel {
  id: string
  project_name: string
  keywords_count: number
  locations: { [key: string]: number }
}

type AuthState = {
  authenticated: boolean | null
  user: IUserModel
  oauthUser: any | null
  tariffDetails: {
    tariff?: ITariffModel
    location_details: { [key: string]: number }
    project_details?: IProjectDetailsModel[]
    tariffUpdated?: Date
  }
}

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    authenticated: null,
    tariffDetails: {
      tariffUpdated: undefined,
    },
  } as AuthState,
  reducers: {
    login: (state: AuthState, { payload }: PayloadAction<IUserModel>) => {
      state.authenticated = payload.status === 'active'
      state.oauthUser = payload
      state.user = payload
    },
    logOut: (state: AuthState) => {
      state.authenticated = false
      state.oauthUser = undefined
    },
    setSignup: (state: AuthState, { payload }: PayloadAction<any>) => {
      state.oauthUser = payload
    },
    setTariffDetails: (state: AuthState, { payload }: PayloadAction<any>) => {
      state.tariffDetails = { ...state.tariffDetails, ...payload }
    },
    setTariffUpdated: (state: AuthState) => {
      state.tariffDetails = { ...state.tariffDetails, tariffUpdated: new Date() }
    },
    removeNotification: (
      state: AuthState,
      { payload }: PayloadAction<'onboarding_tutorial' | 'explore_demo_reports_block' | string>,
    ) => {
      state.user.notifications = state.user.notifications.filter(
        (notification) => notification.type !== payload,
      )
    },
  },
})

export const { login, logOut, setSignup, setTariffDetails, setTariffUpdated, removeNotification } =
  authSlice.actions

export const selectAuthenticated = (state: RootState) => state.root.auth.authenticated
export const selectUser = (state: RootState) => state.root.auth.user
export const selectOauthUser = (state: RootState) => state.root.auth.oauthUser
export const selectTariffDetails = (state: RootState) => state.root.auth.tariffDetails
export const selectTariffUpdated = (state: RootState) => state.root.auth.tariffDetails.tariffUpdated
export const selectSubRole = (state: RootState) => state.root.auth.user.sub_role
export const selectNotifications = (state: RootState) => state.root.auth.user.notifications

export default authSlice.reducer
